import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from './button';

export interface PaginationProps {
  current?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number, pageSize: number) => void;
  showSizeChanger?: boolean;
  showTotal?: boolean;
  pageSizeOptions?: Array<number | string>;
  className?: string;
  disabled?: boolean;
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

function getPageItems(current: number, pageCount: number) {
  const currentIndex = current - 1;

  if (pageCount <= 6) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  if (currentIndex < 2) {
    return [1, 2, 3, 'right-dots', pageCount - 2, pageCount - 1, pageCount];
  }

  if (currentIndex > pageCount - 3) {
    return [1, 2, 3, 'left-dots', pageCount - 2, pageCount - 1, pageCount];
  }

  return [
    1,
    'left-dots',
    current - 1,
    current,
    current + 1,
    'right-dots',
    pageCount,
  ];
}

export function Pagination({
  current = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  total = 0,
  onChange,
  showSizeChanger = true,
  showTotal = true,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  className,
  disabled,
}: PaginationProps) {
  const { t } = useTranslation();
  const safePageSize = pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / safePageSize));
  const safeCurrent = Math.min(Math.max(current, 1), pageCount);
  const canPreviousPage = safeCurrent > 1 && !disabled;
  const canNextPage = safeCurrent < pageCount && !disabled;
  const pageItems = getPageItems(safeCurrent, pageCount);
  const normalizedPageSizeOptions = pageSizeOptions.map(option =>
    Number(option),
  );

  const changePage = (page: number) => {
    if (disabled) return;
    const nextPage = Math.min(Math.max(page, 1), pageCount);
    if (nextPage !== safeCurrent) {
      onChange?.(nextPage, safePageSize);
    }
  };

  const changePageSize = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled) return;
    onChange?.(safeCurrent, Number(event.target.value));
  };

  return (
    <div
      className={cn(
        'flex w-full items-center justify-end px-0 pt-9 font-["Be_Vietnam_Pro",sans-serif]',
        className,
      )}
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-4 text-[13px] text-[#111111]">
        {showSizeChanger ? (
          <label className="flex items-center gap-2 font-medium">
            <span>{t('b2b.rowsPerPage1', 'Hiển thị')}</span>
            <select
              value={safePageSize}
              onChange={changePageSize}
              disabled={disabled}
              className="h-8 w-[65px] cursor-pointer rounded border border-[#e5e7eb] bg-white px-2 text-[13px] font-medium text-[#111111] outline-none transition-colors hover:border-[#f06f24] focus:border-[#f06f24] disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-[#bfbfbf]"
              aria-label={t('b2b.rowsPerPage1', 'Hiển thị')}
            >
              {normalizedPageSizeOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {showTotal ? (
              <span>
                {t('b2b.rowsPerPage2', {
                  total,
                  defaultValue: t('b2b.totalRows', {
                    total,
                    defaultValue: 'trên {{total}} bản ghi',
                  }),
                })}
              </span>
            ) : null}
          </label>
        ) : (
          <span />
        )}

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <Button
            type="button"
            variant="outline"
            aria-label={t('b2b.previous', 'Trang trước')}
            className="h-8 min-w-[104px] rounded border border-[#e5e7eb] bg-white px-3 text-[13px] font-medium text-[#111111] shadow-none hover:bg-[#fff7e6] hover:text-[#111111] disabled:cursor-not-allowed disabled:border-[#ededed] disabled:bg-[#f5f5f5] disabled:text-[#8c8c8c]"
            classBtn="gap-1.5"
            onClick={() => changePage(safeCurrent - 1)}
            disabled={!canPreviousPage}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{t('b2b.previous', 'Trang trước')}</span>
          </Button>

          <div className="flex items-center gap-1">
            {pageItems.map(item =>
              typeof item === 'number' ? (
                <Button
                  key={item}
                  type="button"
                  variant="outline"
                  aria-label={t('b2b.goToPage', 'Go to page {{page}}', {
                    page: item,
                  })}
                  aria-current={item === safeCurrent ? 'page' : undefined}
                  className={cn(
                    'h-8 min-w-8 rounded border-0 bg-white px-3 text-sm font-medium text-[#111111] shadow-none hover:bg-[#fff7e6] hover:text-[#111111] disabled:cursor-not-allowed disabled:bg-white disabled:text-[#111111]',
                    item === safeCurrent &&
                      'border border-[#9f4b44] bg-white text-[#111111] hover:!border-[#9f4b44] hover:bg-white hover:text-[#111111] hover:shadow-none disabled:border-[#9f4b44] disabled:text-[#111111]',
                  )}
                  onClick={() => changePage(item)}
                  disabled={disabled || item === safeCurrent}
                >
                  {item}
                </Button>
              ) : (
                <span
                  key={item}
                  className="flex h-8 min-w-8 items-center justify-center px-3 font-medium text-[#8c8c8c]"
                >
                  ...
                </span>
              ),
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            aria-label={t('b2b.next', 'Trang sau')}
            className="h-8 min-w-[94px] rounded border border-[#e5e7eb] bg-white px-3 text-[13px] font-medium text-[#111111] shadow-none hover:bg-[#fff7e6] hover:text-[#111111] disabled:cursor-not-allowed disabled:border-[#ededed] disabled:bg-[#f5f5f5] disabled:text-[#8c8c8c]"
            classBtn="gap-1.5"
            onClick={() => changePage(safeCurrent + 1)}
            disabled={!canNextPage}
          >
            <span>{t('b2b.next', 'Trang sau')}</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Pagination;
